import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Variant } from '@/redux/slices/pdpSlice';
import { UpcomingLiveProps } from '..';
import BaseBox from '../../components/BaseBox';
import ActionButton from './ActionButton';
import OnDemandCard from './OnDemandCard';
import VariantList from './VariantList';
import { getDiscountAdviceMessage } from '../../../helpers';
import { useContentOptOut } from '@/hooks/PDP/useContentOptOut';

interface VariantsSelectorProps {
  labels: UpcomingLiveProps['labels'];
  softSelection: Variant;
  isHardSelection?: boolean;
  isModal?: boolean;
  programCard?: UpcomingLiveProps['onDemandProgram'];
  onVariantSelect?: (variant: Variant) => void;
  onSubmit: (variant: Variant) => void;
  onJoinWaitlist?: (variant: Variant) => Promise<boolean | void> | boolean | void;
  isSuccess?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  alreadyOnWaitlistCodes?: Set<string>;
  isWaitlistRegistrationsPending?: boolean;
  waitlistMailToString?: string;
  onWaitlistVariantSelect?: (variant?: Variant) => void;
  withinPanel?: boolean;
  withinTabs?: boolean;
}

const VariantsSelector = forwardRef<HTMLButtonElement, VariantsSelectorProps>(
  (
    {
      labels,
      softSelection,
      isHardSelection,
      isModal,
      programCard,
      onVariantSelect,
      onSubmit,
      isSuccess,
      isLoading,
      isError,
      alreadyOnWaitlistCodes,
      isWaitlistRegistrationsPending,
      waitlistMailToString,
      onWaitlistVariantSelect,
      onJoinWaitlist,
      withinPanel,
      withinTabs
    },
    ref
  ) => {
    const { contentOptOut } = useContentOptOut();
    const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(
      isHardSelection ? softSelection : undefined
    );
    const [showError, setShowError] = useState(false);
    const activeVariant = selectedVariant ?? softSelection;

    const selectedItemCode = activeVariant?.code ? String(activeVariant.code).trim() : undefined;
    const isAlreadyOnWaitlist =
      !!selectedItemCode && !!alreadyOnWaitlistCodes?.has(selectedItemCode);
    const shouldHideJoinWaitlist =
      !!activeVariant?.waitlist && (isAlreadyOnWaitlist || !!isWaitlistRegistrationsPending);

    const handleSelect = useCallback(
      (variant: Variant) => {
        setSelectedVariant(variant);
        if (onVariantSelect) onVariantSelect(variant);
        if (onWaitlistVariantSelect) onWaitlistVariantSelect(variant);
      },
      [onVariantSelect, onWaitlistVariantSelect]
    );

    const handleSubmit = useCallback(async () => {
      setShowError(!selectedVariant);
      if (!selectedVariant) return;

      if (selectedVariant.waitlist && selectedVariant.retail !== false) {
        let shouldOpenMailTo = true;
        try {
          const waitlistResult = await onJoinWaitlist?.(selectedVariant);
          shouldOpenMailTo = waitlistResult !== false;
        } catch {
          shouldOpenMailTo = false;
        }
        if (shouldOpenMailTo && waitlistMailToString) window.location.href = waitlistMailToString;
        return;
      }

      onSubmit(selectedVariant);
    }, [onJoinWaitlist, onSubmit, selectedVariant, waitlistMailToString]);

    useEffect(() => {
      if (selectedVariant) setShowError(false);
    }, [selectedVariant]);

    return (
      <>
        <BaseBox.Content withinPanel={withinPanel} withinTabs={withinTabs} inModal={isModal}>
          <VariantList
            labels={labels}
            selectedVariant={selectedVariant}
            handleSelect={handleSelect}
            alreadyOnWaitlistCodes={alreadyOnWaitlistCodes}
            isWaitlistRegistrationsPending={isWaitlistRegistrationsPending}
            withinPanel={withinPanel}
          />
          {isAlreadyOnWaitlist && (
            <div className="mx-auto mt-6 max-w-[342px] px-6">
              <p className="text-2-fixed-medium text-center leading-[15px] text-gray-dark">
                Please contact Customer Service to be removed from waitlist at 800.260.4PLI or{' '}
                <a href="mailto:info@pli.edu" className="text-gray-dark no-underline">
                  info@pli.edu
                </a>
                .
              </p>
            </div>
          )}
        </BaseBox.Content>
        <BaseBox.Footer
          primaryButton={
            <ActionButton
              ref={ref}
              labels={labels}
              selectedVariant={selectedVariant ?? softSelection}
              onClick={handleSubmit}
              isSuccess={isSuccess}
              isLoading={isLoading}
              isError={isError}
              hideJoinWaitlist={shouldHideJoinWaitlist}
            />
          }
          noSelectionError={showError && labels.noSelectionMsg}
          discountAdviceMessage={getDiscountAdviceMessage(
            selectedVariant ?? softSelection,
            labels,
            contentOptOut
          )}
          extraMessage={labels.extraMessage}
          waitlistMessage={
            selectedVariant?.waitlist &&
            !isAlreadyOnWaitlist &&
            selectedVariant.retail !== false &&
            !!waitlistMailToString &&
            labels.waitlistMessage.replace('mailto://', waitlistMailToString)
          }
          codeLabel={labels.programId}
          codeValue={selectedVariant ? selectedVariant.code! : softSelection.code!}
          withinPanel={withinPanel}
        ></BaseBox.Footer>
        {!!programCard && <OnDemandCard {...programCard} />}
      </>
    );
  }
);

export default VariantsSelector;
